import { useState } from "react";
import { Container } from "react-grid-system";
import { useMediaQuery } from "react-responsive";

import {
  Button,
  Card,
  Link as DSLink,
  Heading,
  Icon,
  Span,
} from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { screenSizes } from "consts";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import {
  WindDefenseContext,
  type WindDefenseContextState,
} from "pages/offer/wind-defense/wind-defense-flow";
import { trackEvent, useFlags } from "utils";

import styles from "./shingle-reseal-landing.module.scss";
import { WaiverTermsModal } from "./waiver-terms";

export const ShingleResealLanding = () => {
  const { windDefenseReduceFlow } = useFlags();
  const [showSageSureWaiver, setShowSageSureWaiver] = useState(false);
  const [showLiftLockTermsOfService, setShowLiftLockTermsOfService] =
    useState(false);
  const isLaptop = useMediaQuery({ maxWidth: screenSizes.laptopL });
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });
  const { formNavigate, state } =
    useMultiStepFormState<WindDefenseContextState>(WindDefenseContext);

  const ssWaiverContent = state?.termsSageSure;
  const liftLockTermsContent = state?.termsLiftLock;

  const Disclaimer = () => {
    return (
      <div className={styles.disclaimer}>
        <Span size="md" color="quiet">
          By enrolling above, you have read and agreed to the terms and
          conditions of the{" "}
          <Button
            className={styles.wdTermsLink}
            onPress={() => {
              setShowSageSureWaiver(true);
            }}
            appearance="link"
            size="default"
          >
            SageSure Waiver
          </Button>{" "}
          and{" "}
          <Button
            className={styles.wdTermsLink}
            onPress={() => {
              setShowLiftLockTermsOfService(true);
            }}
            appearance="link"
            size="default"
          >
            LiftLock's Terms of Service
          </Button>
          .
        </Span>
      </div>
    );
  };

  return (
    <BaseLayout hidePolicyHeader={isMobile} hideFooter={isMobile}>
      <Container className={styles.shingleResealingContinaer}>
        <Stack>
          <Card className={styles.sectionWrapperDS}>
            <Stack
              gap={isLaptop ? "xl" : "2xl"}
              className={styles.introContent}
            >
              <Heading size={isLaptop && !isMobile ? "md" : "lg"}>
                Help secure your roof against severe weather
              </Heading>
              <Span size="lg" color="quiet">
                Even newer roofs can have loose shingles. In a hurricane, that
                can mean tens of thousands in damage. This free service helps
                you stay one step ahead.
              </Span>
              <Stack gap={isLaptop ? "md" : "2xl"}>
                <Heading size="md">How do I participate?</Heading>
                <ol className={styles.steps}>
                  <li className={styles.listItem}>
                    <div className={styles.stepNumber}>
                      <Heading size="sm">1</Heading>
                    </div>
                    <Span size="lg">
                      Sign up for a chance to be selected for this free service
                    </Span>
                  </li>
                  <li className={styles.listItem}>
                    <div className={styles.stepNumber}>
                      <Heading size="sm">2</Heading>
                    </div>
                    <Span size="lg">
                      A licensed LiftLock technician will get in touch with you
                      to schedule a visit if your home is selected.
                    </Span>
                  </li>
                  <li className={styles.listItem}>
                    <div className={styles.stepNumber}>
                      <Heading size="sm">3</Heading>
                    </div>
                    <Span size="lg">
                      A trained professional will clean gutters, clear roof
                      debris, and secure loose shingles
                    </Span>
                  </li>
                </ol>
              </Stack>
              <div className={styles.desktopButton}>
                <Button
                  onPress={() => {
                    if (windDefenseReduceFlow) {
                      trackEvent("Wind Defense Reduce Flow: Sign Up Clicked");
                    } else {
                      trackEvent("shinglesForm_landing_JoinClicked");
                    }
                    formNavigate("policy-confirmation");
                  }}
                  data-testid="get-started-btn"
                >
                  Sign up
                </Button>
              </div>
              {!!windDefenseReduceFlow && (
                <div className={styles.reduceFlowDisclaimer}>
                  <Disclaimer />
                </div>
              )}
            </Stack>
            <div
              className={styles.imageWrapper}
              role="img"
              aria-label="Roof shingles"
            ></div>
          </Card>
          <Card className={styles.wdHowDS}>
            <Stack className={styles.howWrapper}>
              <Heading size="lg">How does Wind Defense work?</Heading>
              <Span size="lg" color="quiet">
                SageSure works with LiftLock to prepare your roof to withstand
                high winds
              </Span>
              <div className={styles.hwdWork}>
                <Card className={styles.hwdWorkItemsWrapper}>
                  <div className={styles.cleanGuttersImage}></div>
                  <Heading size="sm">Clean gutters</Heading>
                  <Span size="md" color="quiet">
                    Full gutters can overflow and cause water damage during a
                    storm.
                  </Span>
                </Card>
                <Card className={styles.hwdWorkItemsWrapper}>
                  <div className={styles.removeRoofDebrisImage}></div>
                  <Heading size="sm">Remove roof debris</Heading>
                  <Span size="md" color="quiet">
                    Roof debris can damage roof covering and nearby homes during
                    a storm.
                  </Span>
                </Card>
                <Card className={styles.hwdWorkItemsWrapper}>
                  <div className={styles.secureLooseShinglesImage}></div>
                  <Heading size="sm">Secure loose shingles</Heading>
                  <Span size="md" color="quiet">
                    Loose shingles can lift in strong winds causing significant
                    roof cover damage.
                  </Span>
                </Card>
              </div>
            </Stack>
          </Card>
          <Card className={styles.wdHowDS}>
            <Stack className={styles.howWrapper}>
              <Heading size="lg" className={styles.testimonialsHeader}>
                See Wind Defense in Action
              </Heading>
              <div className={styles.testimonials}>
                <Card className={styles.testimonialItems}>
                  <div className={styles.testimonialIconHeader}>
                    <Icon name="PersonWithMessage" size="lg"></Icon>
                  </div>
                  <Span size="lg" className={styles.testimonials}>
                    “We had over 450 shingles lifting on what seemingly looked
                    like a roof that was in excellent condition. Thank you
                    LiftLock, and thank you SageSure.”
                  </Span>
                  <p className={styles.testimonialFrom}>- Danielle D.</p>
                </Card>
                <Card className={styles.testimonialItems}>
                  <div className={styles.testimonialIconHeader}>
                    <Icon name="PersonWithMessage" size="lg"></Icon>
                  </div>
                  <Span size="lg" className={styles.testimonials}>
                    “Technician Junior was on time, very professional, very
                    courteous, very knowledgeable, and customer service Jackson
                    was equally the same. Thank you LiftLock.”
                  </Span>
                  <p className={styles.testimonialFrom}>- Clem B.</p>
                </Card>
                <Card className={styles.testimonialItems}>
                  <div className={styles.testimonialIconHeader}>
                    <Icon name="PersonWithMessage" size="lg"></Icon>
                  </div>
                  <Span size="lg" className={styles.testimonials}>
                    “I very much appreciate the work he did and I believe the
                    work done by your company via my insurance company should
                    prove invaluable.”
                  </Span>
                  <p className={styles.testimonialFrom}>- John T.</p>
                </Card>
              </div>
            </Stack>
          </Card>
          {!windDefenseReduceFlow ? (
            <div className={styles.notificationDisclaimer}>
              <Span color="quiet">
                Wind Defense is not available in all areas. Selection is
                dependent on service availability and other factors. There is no
                guarantee that you will receive the service by signing up. Have
                more questions? Learn more with{" "}
                <DSLink
                  href="https://sagesure.widen.net/s/khnx5fkmrq/liftlock-wind-defense-research-program-faq"
                  target="_blank"
                >
                  Wind Defense FAQs
                </DSLink>
              </Span>
            </div>
          ) : null}
        </Stack>
      </Container>
      <Stack gap="md" className={styles.mobileFooter}>
        <Button
          onPress={() => {
            if (windDefenseReduceFlow) {
              trackEvent("Wind Defense Reduce Flow: Sign Up Clicked");
            } else {
              trackEvent("shinglesForm_landing_JoinClicked");
            }
            formNavigate("policy-confirmation");
          }}
          fluid
        >
          Sign up
        </Button>
        {!!windDefenseReduceFlow && <Disclaimer />}
      </Stack>
      <WaiverTermsModal
        header="SageSure Waiver"
        content={ssWaiverContent}
        show={showSageSureWaiver}
        onClose={() => setShowSageSureWaiver(false)}
      />
      <WaiverTermsModal
        header="LiftLock's Terms of Service"
        content={liftLockTermsContent}
        show={showLiftLockTermsOfService}
        onClose={() => setShowLiftLockTermsOfService(false)}
      />
    </BaseLayout>
  );
};
